/**
 * Cost Detail Modal Component
 * Displays detailed breakdown for a selected cost item including employee damages
 */
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  AlertTriangle,
  TrendingUp,
  Users,
  MapPin,
  Scale,
  Calculator,
  FileText,
  Info
} from "lucide-react";

const CostDetailModal = ({
  selectedDetail,
  onClose,
  detailPeriod,
  setDetailPeriod,
  formatDetailCurrency,
  generateEmployeeBreakdown,
  hourlyRate
}) => {
  const navigate = useNavigate();

  if (!selectedDetail) return null;

  return (
    <Dialog open={!!selectedDetail} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-xl font-bold flex items-center gap-2">
                <AlertTriangle className={`w-5 h-5 ${selectedDetail.type === "risk" ? "text-red-500" : "text-amber-500"}`} />
                {selectedDetail.ruleName}
              </DialogTitle>
              <DialogDescription className="flex items-center gap-2 mt-1">
                <MapPin className="w-3 h-3" />
                {selectedDetail.state || "Multiple States"} • {selectedDetail.company}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 pr-2">
          {/* Violation Type */}
          <Card className="bg-secondary/30">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${selectedDetail.type === "risk" ? "bg-red-100 dark:bg-red-950/50" : "bg-amber-100 dark:bg-amber-950/50"}`}>
                  <Scale className={`w-5 h-5 ${selectedDetail.type === "risk" ? "text-red-600" : "text-amber-600"}`} />
                </div>
                <div>
                  <p className="font-semibold">{selectedDetail.category}</p>
                  <p className="text-sm text-muted-foreground">
                    {selectedDetail.type === "risk" 
                      ? "Non-compliant configuration detected - potential liability exposure"
                      : "Over-compliant configuration - excess labor costs identified"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Damages Formula */}
          {selectedDetail.type === "risk" && selectedDetail.law && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Calculator className="w-4 h-4" />
                  Damages Formula
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-center gap-2 text-sm bg-secondary/50 p-3 rounded-lg">
                  <span className="font-mono bg-background px-2 py-1 rounded">${selectedDetail.hourlyRate?.toFixed(2) || hourlyRate}</span>
                  <span className="text-muted-foreground">×</span>
                  <span className="font-mono bg-background px-2 py-1 rounded">{selectedDetail.law.WageMultiplier || 1}x</span>
                  <span className="text-muted-foreground">×</span>
                  <span className="font-mono bg-background px-2 py-1 rounded">Violation Hrs</span>
                  <span className="text-muted-foreground">=</span>
                  <span className="font-mono bg-red-100 dark:bg-red-950/50 text-red-600 px-2 py-1 rounded font-semibold">Back Wages</span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* State Statute Details */}
          {selectedDetail.law && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  State Statute Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Statute Reference:</span>
                    <span className="font-medium">{selectedDetail.law.StatuteReference || "N/A"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Wage Multiplier:</span>
                    <span className="font-medium">{selectedDetail.law.WageMultiplier || 1}x</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Liquidated Damages:</span>
                    <span className="font-medium">{selectedDetail.law.LiquidatedDamagesMultiplier || 0}x</span>
                  </div>
                  {selectedDetail.law.CivilPenaltyMinPerViolation > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Civil Penalty Range:</span>
                      <span className="font-medium">
                        ${selectedDetail.law.CivilPenaltyMinPerViolation} - ${selectedDetail.law.CivilPenaltyMaxPerViolation}
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Exposure Summary */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Exposure Summary
                </CardTitle>
                <Tabs value={detailPeriod} onValueChange={setDetailPeriod}>
                  <TabsList className="h-7">
                    <TabsTrigger value="monthly" className="text-xs px-2 h-5">Monthly</TabsTrigger>
                    <TabsTrigger value="quarterly" className="text-xs px-2 h-5">Quarterly</TabsTrigger>
                    <TabsTrigger value="annual" className="text-xs px-2 h-5">Annual</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-3 bg-secondary/30 rounded-lg">
                  <p className="text-xs text-muted-foreground">Total Potential</p>
                  <p className={`text-xl font-bold ${selectedDetail.type === "risk" ? "text-red-600" : "text-amber-600"}`}>
                    {formatDetailCurrency(selectedDetail.amount)}
                  </p>
                </div>
                <div className="text-center p-3 bg-secondary/30 rounded-lg">
                  <p className="text-xs text-muted-foreground">Back Wages</p>
                  <p className="text-xl font-bold">
                    {formatDetailCurrency(selectedDetail.baseDamage || 0)}
                  </p>
                </div>
                <div className="text-center p-3 bg-secondary/30 rounded-lg">
                  <p className="text-xs text-muted-foreground">Employees</p>
                  <p className="text-xl font-bold">{selectedDetail.employees}</p>
                </div>
                <div className="text-center p-3 bg-secondary/30 rounded-lg">
                  <p className="text-xs text-muted-foreground">Per Employee</p>
                  <p className="text-xl font-bold">
                    {formatDetailCurrency(selectedDetail.amount / selectedDetail.employees)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Why Non-Compliant */}
          {selectedDetail.type === "risk" && (
            <Card className="border-red-200 dark:border-red-900/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2 text-red-600">
                  <Info className="w-4 h-4" />
                  Why {selectedDetail.type === "risk" ? "Non-Compliant" : "Over-Compliant"}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  This pay rule configuration does not meet the {selectedDetail.state || "state"} requirements for {selectedDetail.category?.toLowerCase() || "compliance"}. 
                  Based on the current settings, affected employees may be entitled to back wages, liquidated damages, 
                  and potential civil penalties as defined by state law.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Employee Damages Breakdown */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Users className="w-4 h-4" />
                Employee Damages Breakdown
              </CardTitle>
              <CardDescription className="text-xs">
                Estimated damages by employee ({selectedDetail.employees > 50 ? "showing first 50" : `${selectedDetail.employees} employees`})
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[200px]">
                <table className="data-table text-xs w-full">
                  <thead className="sticky top-0 bg-card z-10">
                    <tr>
                      <th className="py-2 text-left">Employee ID</th>
                      <th className="py-2 text-left">State</th>
                      <th className="py-2 text-right">Hourly Rate</th>
                      <th className="py-2 text-right">{selectedDetail.law?.ViolationType === "Meal Break" ? "Meal Break Hrs" : selectedDetail.law?.ViolationType === "Rest Break" ? "Rest Break Hrs" : "Violation Hrs"}</th>
                      <th className="py-2 text-right">Back Wages</th>
                      <th className="py-2 text-right">Total (max)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {generateEmployeeBreakdown(selectedDetail).map((emp) => (
                      <tr key={emp.id} className="border-t border-border/50">
                        <td className="py-1.5 font-mono">{emp.id}</td>
                        <td className="py-1.5">{emp.state}</td>
                        <td className="py-1.5 text-right">${emp.hourlyRate.toFixed(2)}</td>
                        <td className="py-1.5 text-right">{emp.violationHours.toFixed(1)}</td>
                        <td className="py-1.5 text-right">{formatDetailCurrency(emp.backWages)}</td>
                        <td className={`py-1.5 text-right font-semibold ${selectedDetail.type === "risk" ? "text-red-600" : "text-amber-600"}`}>
                          {formatDetailCurrency(emp.total)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Total */}
          <div className={`p-4 rounded-lg ${selectedDetail.type === "risk" ? "bg-red-50 dark:bg-red-950/30" : "bg-amber-50 dark:bg-amber-950/30"}`}>
            <div className="flex items-center justify-between">
              <span className="font-medium">Total Potential {selectedDetail.type === "risk" ? "Damages" : "Savings Opportunity"}</span>
              <span className={`text-2xl font-bold ${selectedDetail.type === "risk" ? "text-red-600" : "text-amber-600"}`}>
                {formatDetailCurrency(selectedDetail.amount)}
              </span>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex-shrink-0 flex justify-between pt-4 border-t">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate(`/pay-rule/${encodeURIComponent(selectedDetail.payRuleId)}`)}
          >
            View Pay Rule Details
          </Button>
          <Button 
            variant="default" 
            size="sm"
            onClick={onClose}
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CostDetailModal;
